import {
  buildInvestmentCreateRequest,
  buildInvestmentTermsRequest,
  buildInvestmentUpdateRequest,
} from "@/application/mappers/investments/investmentFormMapper";
import {
  useCreateInvestment,
  useCreateInvestmentFromProposal,
  useUpdateInvestment,
} from "@/application/queries";
import type { InvestmentResponse } from "@/domain/investment";
import type { InvestorSummary } from "@/domain/investor";
import type { FiscalQuota } from "@/domain/beneficiary";
import type { InvestmentFormData } from "@/domain/validation/investmentSchemas";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import type { InvestmentForm } from "@/application/workflows/investments/investmentForm.types";

interface UseInvestmentSubmitProps {
  form: InvestmentForm;
  isEditMode: boolean;
  existingInvestment: InvestmentResponse | null;
  proposalUuid: string | null;
  selectedInvestor: InvestorSummary | null;
  selectedQuota: FiscalQuota | null;
  onInvestmentAdded: (uuid: string) => void;
  onClose: () => void;
  resetForm: () => void;
  describeError: (error: unknown) => string;
}

export function useInvestmentSubmit(props: UseInvestmentSubmitProps) {
  const {
    form,
    isEditMode,
    existingInvestment,
    proposalUuid,
    selectedInvestor,
    selectedQuota,
    onInvestmentAdded,
    onClose,
    resetForm,
    describeError,
  } = props;

  const orgId = useOrganizationStore((state) => state.selectedOrganizationUuid ?? "");
  const createInvestmentMutation = useCreateInvestment(orgId);
  const createFromProposalMutation = useCreateInvestmentFromProposal(orgId);
  const updateInvestmentMutation = useUpdateInvestment(orgId);

  const onSubmit = form.handleSubmit(async (values: InvestmentFormData) => {
    if (!selectedInvestor || !selectedQuota) return;
    form.clearErrors("root");
    try {
      let uuid = existingInvestment?.uuid ?? "";
      if (isEditMode && existingInvestment) {
        const updateData = buildInvestmentUpdateRequest(values, selectedQuota.uuid);
        await updateInvestmentMutation.mutateAsync({
          uuid: existingInvestment.uuid,
          data: updateData,
        });
      } else if (proposalUuid) {
        const terms = buildInvestmentTermsRequest(values, selectedQuota.uuid);
        uuid = (await createFromProposalMutation.mutateAsync({ proposalUuid, terms })).uuid;
      } else {
        const createData = buildInvestmentCreateRequest(
          values,
          selectedInvestor.uuid,
          selectedQuota.uuid,
        );
        uuid = (await createInvestmentMutation.mutateAsync(createData)).uuid;
      }
      onInvestmentAdded(uuid);
      onClose();
      resetForm();
    } catch (error: unknown) {
      form.setError("root", { message: describeError(error) });
    }
  });

  return {
    onSubmit,
    isSubmitting:
      createInvestmentMutation.isPending ||
      createFromProposalMutation.isPending ||
      updateInvestmentMutation.isPending,
  };
}
